import React, { useCallback, useEffect, useState } from "react";
import * as Survey from "survey-core";
import {
  ReactQuestionFactory,
  SurveyQuestionElementBase,
} from "survey-react-ui";
import { ageToDateOfBirth } from "../../../landers/Utils/ageToDateOfBirth";

const CUSTOM_TYPE = "ageinput";
const RETIREMENT_DATE_TYPE = "retirementdate";
const MIN_AGE = 1;
const MAX_AGE = 100;

const retirementMonthChoices = [
  { value: "01", text: "January" },
  { value: "02", text: "February" },
  { value: "03", text: "March" },
  { value: "04", text: "April" },
  { value: "05", text: "May" },
  { value: "06", text: "June" },
  { value: "07", text: "July" },
  { value: "08", text: "August" },
  { value: "09", text: "September" },
  { value: "10", text: "October" },
  { value: "11", text: "November" },
  { value: "12", text: "December" },
];

function getRetirementYearChoices() {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: 16 }, (_, index) => String(currentYear + index));
}

function buildRetirementDate(month: string, year: string) {
  return month && year ? `${year}-${month}-01` : "";
}

function splitRetirementDate(value: string | undefined) {
  const match = /^(\d{4})-(\d{2})-\d{2}$/.exec(value || "");
  if (!match) return null;
  return { month: match[2], year: match[1] };
}

function getSurvey(question: Survey.Question): Survey.SurveyModel | null {
  return question.survey as Survey.SurveyModel | null;
}

function dateOfBirthToAge(dateOfBirth: string | undefined): string {
  if (!dateOfBirth) return "";
  const match = /^(\d{4})-\d{2}-\d{2}$/.exec(dateOfBirth);
  if (!match) return "";
  const birthYear = Number(match[1]);
  const age = new Date().getFullYear() - birthYear;
  return Number.isFinite(age) && age > 0 ? String(age) : "";
}

export class QuestionAgeInputModel extends Survey.Question {
  getType() {
    return CUSTOM_TYPE;
  }

  get ageValue(): string {
    return this.getPropertyValue("ageValue", "");
  }
  set ageValue(val: string) {
    this.setPropertyValue("ageValue", val);
  }

  /** Lowest and highest accepted age; schemas can narrow the default 1-100. */
  get minAge(): number {
    return this.getPropertyValue("minAge", MIN_AGE);
  }
  set minAge(val: number) {
    this.setPropertyValue("minAge", val);
  }
  get maxAge(): number {
    return this.getPropertyValue("maxAge", MAX_AGE);
  }
  set maxAge(val: number) {
    this.setPropertyValue("maxAge", val);
  }
  get placeholder(): string {
    return this.getPropertyValue("placeholder", "");
  }
  set placeholder(val: string) {
    this.setPropertyValue("placeholder", val);
  }

  validate(): boolean {
    this.clearErrors();

    const ageText = this.ageValue || dateOfBirthToAge(this.value as string | undefined);

    if (!this.isRequired && !ageText) {
      return true;
    }

    if (!ageText) {
      this.errors.push(new Survey.CustomError("Please enter your age"));
      return false;
    }

    const ageNumber = Number(ageText);
    if (!Number.isFinite(ageNumber) || ageNumber < this.minAge || ageNumber > this.maxAge) {
      this.errors.push(
        new Survey.CustomError(`Please enter an age between ${this.minAge} and ${this.maxAge}`)
      );
      return false;
    }

    return true;
  }
}

Survey.Serializer.addClass(
  CUSTOM_TYPE,
  [
    { name: "minAge:number", default: MIN_AGE },
    { name: "maxAge:number", default: MAX_AGE },
    { name: "placeholder" },
  ],
  () => new QuestionAgeInputModel(""),
  "question"
);

Survey.ElementFactory.Instance.registerElement(CUSTOM_TYPE, (name) => {
  return new QuestionAgeInputModel(name);
});

function AgeInputQuestion(props: { question: QuestionAgeInputModel }) {
  const { question } = props;
  const [ageText, setAgeText] = useState(
    () => question.ageValue || dateOfBirthToAge(question.value as string | undefined)
  );

  useEffect(() => {
    const nextAgeText = question.ageValue || dateOfBirthToAge(question.value as string | undefined);
    setAgeText(nextAgeText);
  }, [question, question.ageValue, question.value]);

  const isReadOnly = question.readOnly || question.isReadOnly;
  const hasError = question.errors.length > 0;
  const inputClassName = `sd-input sd-text ${hasError ? "sd-input--error" : ""} ${
    isReadOnly ? "sd-input--readonly" : ""
  }`;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const digitsOnly = event.target.value.replace(/\D/g, "").slice(0, 3);
    setAgeText(digitsOnly);
    question.ageValue = digitsOnly;

    const ageNumber = Number(digitsOnly);
    const isPlausibleAge = digitsOnly !== "" && Number.isFinite(ageNumber) && ageNumber > 0;
    question.value = isPlausibleAge ? ageToDateOfBirth(digitsOnly) ?? "" : "";
  };

  return (
    <div className="sd-question__content">
      <input
        type="tel"
        inputMode="numeric"
        pattern="[0-9]*"
        className={inputClassName}
        value={ageText}
        onChange={handleChange}
        readOnly={isReadOnly}
        placeholder={question.placeholder || undefined}
        aria-label={question.title || "Your age"}
      />
    </div>
  );
}

class SurveyQuestionAgeInput extends SurveyQuestionElementBase {
  get question() {
    return this.questionBase as QuestionAgeInputModel;
  }

  renderElement() {
    return <AgeInputQuestion question={this.question} />;
  }
}

ReactQuestionFactory.Instance.registerQuestion(CUSTOM_TYPE, (props) => {
  return React.createElement(SurveyQuestionAgeInput, props);
});

export class QuestionRetirementDateModel extends Survey.Question {
  getType() {
    return RETIREMENT_DATE_TYPE;
  }

  validate(): boolean {
    this.clearErrors();

    const survey = getSurvey(this);
    const storedValue = splitRetirementDate(this.value as string | undefined);
    const month = (survey?.getValue("retirementMonth") as string) || storedValue?.month || "";
    const year = (survey?.getValue("retirementYear") as string) || storedValue?.year || "";

    if (!this.isRequired && !month && !year) {
      return true;
    }

    if (!month || !year) {
      this.errors.push(new Survey.CustomError("Please select when you're planning to retire"));
      return false;
    }

    return true;
  }
}

Survey.Serializer.addClass(
  RETIREMENT_DATE_TYPE,
  [],
  () => new QuestionRetirementDateModel(""),
  "question"
);

Survey.ElementFactory.Instance.registerElement(RETIREMENT_DATE_TYPE, (name) => {
  return new QuestionRetirementDateModel(name);
});

function RetirementDateQuestion(props: { question: QuestionRetirementDateModel }) {
  const { question } = props;
  const storedParts = splitRetirementDate(question.value as string | undefined);
  const survey = getSurvey(question);

  const [month, setMonth] = useState(
    () => (survey?.getValue("retirementMonth") as string) || storedParts?.month || ""
  );
  const [year, setYear] = useState(
    () => (survey?.getValue("retirementYear") as string) || storedParts?.year || ""
  );

  const syncFromSurveyValue = useCallback(() => {
    const currentValue =
      (survey?.getValue(question.name) as string | undefined) ||
      (question.value as string | undefined);
    const nextParts = splitRetirementDate(currentValue);
    const nextMonth = (survey?.getValue("retirementMonth") as string) || nextParts?.month || "";
    const nextYear = (survey?.getValue("retirementYear") as string) || nextParts?.year || "";

    setMonth(nextMonth);
    setYear(nextYear);

    if (nextParts) {
      if (survey?.getValue("retirementMonth") !== nextParts.month) {
        survey?.setValue("retirementMonth", nextParts.month);
      }
      if (survey?.getValue("retirementYear") !== nextParts.year) {
        survey?.setValue("retirementYear", nextParts.year);
      }
    }
  }, [question.name, question.value, survey]);

  useEffect(() => {
    syncFromSurveyValue();
    if (!survey) return;

    const handleValueChanged = (_sender: Survey.SurveyModel, options: { name: string }) => {
      if (
        options.name === question.name ||
        options.name === "retirementMonth" ||
        options.name === "retirementYear"
      ) {
        syncFromSurveyValue();
      }
    };

    survey.onValueChanged.add(handleValueChanged);
    return () => {
      survey.onValueChanged.remove(handleValueChanged);
    };
  }, [question.name, survey, syncFromSurveyValue]);

  // A restored draft is assigned as the survey's whole data after this field mounts, which raises no value-changed
  // event, so pick up a stored month or year (e.g. only the year another lander saved) when the field next renders.
  const storedMonth = String(survey?.getValue("retirementMonth") ?? "");
  const storedYear = String(survey?.getValue("retirementYear") ?? "");
  useEffect(() => {
    if ((storedMonth && storedMonth !== month) || (storedYear && storedYear !== year)) {
      syncFromSurveyValue();
    }
  }, [storedMonth, storedYear, month, year, syncFromSurveyValue]);

  const isReadOnly = question.readOnly || question.isReadOnly;
  const hasError = question.errors.length > 0;
  const selectClassName = `sd-input sd-dropdown ${hasError ? "sd-input--error" : ""} ${
    isReadOnly ? "sd-input--readonly" : ""
  }`;

  const setSurveyValues = (nextMonth: string, nextYear: string) => {
    const nextValue = buildRetirementDate(nextMonth, nextYear);
    question.value = nextValue;
    survey?.setValue("retirementMonth", nextMonth);
    survey?.setValue("retirementYear", nextYear);
  };

  const handleMonthChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const nextMonth = event.target.value;
    setMonth(nextMonth);
    setSurveyValues(nextMonth, year);
  };

  const handleYearChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const nextYear = event.target.value;
    setYear(nextYear);
    setSurveyValues(month, nextYear);
  };

  return (
    <div className="sd-question__content retirement-date-input">
      <select
        className={`${selectClassName} ${month ? "" : "retirement-date-select--placeholder"}`.trim()}
        value={month}
        onChange={handleMonthChange}
        disabled={isReadOnly}
        aria-label="Retirement month"
      >
        <option value="" disabled>
          Month
        </option>
        {retirementMonthChoices.map((option) => (
          <option key={option.value} value={option.value}>
            {option.text}
          </option>
        ))}
      </select>
      <select
        className={`${selectClassName} ${year ? "" : "retirement-date-select--placeholder"}`.trim()}
        value={year}
        onChange={handleYearChange}
        disabled={isReadOnly}
        aria-label="Retirement year"
      >
        <option value="" disabled>
          Year
        </option>
        {getRetirementYearChoices().map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </div>
  );
}

class SurveyQuestionRetirementDate extends SurveyQuestionElementBase {
  get question() {
    return this.questionBase as QuestionRetirementDateModel;
  }

  renderElement() {
    return <RetirementDateQuestion question={this.question} />;
  }
}

ReactQuestionFactory.Instance.registerQuestion(RETIREMENT_DATE_TYPE, (props) => {
  return React.createElement(SurveyQuestionRetirementDate, props);
});
